import { Text } from '@chakra-ui/react'
import { DateTime } from 'luxon'
import { useState } from 'react'

import { isNotNull } from '@/utils/helpers'

import SeriesHostQ from '@/queries/SeriesHostQ'
import SeriesQ from '@/queries/SeriesQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import { SQL } from '@/client/query'
import { RPCRefresh } from '@/client/rpc'
import { SAT_BOOTCAMP_SERIES_TEMPLATE_SLUGS } from '@/client/services/satBootcamp/constants'
import { getPersonalizedSurveyUrl } from '@/client/services/url'
import { SAT_BOOTCAMP_WORLD_SLUG } from '@/client/services/worlds/constants'

import buildSurveyTodoItems, {
  type SurveyTodoConfig,
} from '@/components/Worlds/Todos/hooks/buildSurveyTodoItems'
import type { TodoItem } from '@/components/Worlds/Todos/types'

const SAT_BOOTCAMP_TUTOR_PRE_SERIES_SURVEY_URL = 'https://form.typeform.com/to/GoHPbfax'
const SAT_BOOTCAMP_TUTOR_POST_SERIES_SURVEY_URL = 'https://form.typeform.com/to/KUha2rN3'

const TUTOR_PRE_SURVEY_ESTIMATED_TIME = '10'
const TUTOR_POST_SURVEY_ESTIMATED_TIME = '5-10'
const SHOW_POST_SERIES_SURVEY_DAYS_BEFORE_END = 5
const POST_SERIES_SURVEY_DEADLINE_DAYS_AFTER_END = 8
const DAYS_TO_INCLUDE_SERIES_AFTER_END = 10

type TutorSeriesHostDetails = {
  seriesId: number
  completedPreSeriesSurvey: boolean
  completedPostSeriesSurvey: boolean
  seriesStartDate: DateTime
  seriesEndDate: DateTime
}

function getSurveyTodosForHost({
  hostDetails,
  uuidTraining,
}: {
  hostDetails: TutorSeriesHostDetails
  uuidTraining: string
}): SurveyTodoConfig[] {
  const showPostSeriesSurveyAfter = hostDetails.seriesEndDate.minus({
    days: SHOW_POST_SERIES_SURVEY_DAYS_BEFORE_END,
  })
  const showPostSeriesSurvey = DateTime.now() > showPostSeriesSurveyAfter

  const postSeriesSurveyDeadline = hostDetails.seriesEndDate.plus({
    days: POST_SERIES_SURVEY_DEADLINE_DAYS_AFTER_END,
  })

  const preSurveyLink = getPersonalizedSurveyUrl({
    surveyUrl: SAT_BOOTCAMP_TUTOR_PRE_SERIES_SURVEY_URL,
    uuidTraining,
    worldSlug: SAT_BOOTCAMP_WORLD_SLUG,
  })
  const postSurveyLink = getPersonalizedSurveyUrl({
    surveyUrl: SAT_BOOTCAMP_TUTOR_POST_SERIES_SURVEY_URL,
    uuidTraining,
    worldSlug: SAT_BOOTCAMP_WORLD_SLUG,
  })

  const tutorPreSeriesSurveyTodo: SurveyTodoConfig = {
    title: 'Complete the SAT Bootcamp Pre-Survey',
    subtitle: 'To help us improve the tutor experience!',
    completed: hostDetails.completedPreSeriesSurvey,
    deadline: hostDetails.seriesStartDate,
    modalDetails: {
      key: `sat-bootcamp-series-${hostDetails.seriesId}-tutor-pre-survey`,
      title: 'SAT Bootcamp Pre-Survey',
      body: () => (
        <Text>
          Fill out this {TUTOR_PRE_SURVEY_ESTIMATED_TIME} minute pre-survey for the SAT Bootcamp as
          part of the College Board research study.
        </Text>
      ),
      link: preSurveyLink,
      onMarkComplete: () =>
        RPCRefresh.markSeriesSurveyComplete({
          seriesId: hostDetails.seriesId,
          surveyType: 'tutor-pre-survey',
        }),
    },
  }

  const tutorPostSeriesSurveyTodo: SurveyTodoConfig | null = showPostSeriesSurvey
    ? {
        title: 'Complete the SAT Bootcamp Post-Survey',
        subtitle: 'To help us improve the tutor experience!',
        completed: hostDetails.completedPostSeriesSurvey,
        deadline: postSeriesSurveyDeadline,
        modalDetails: {
          key: `sat-bootcamp-series-${hostDetails.seriesId}-tutor-post-survey`,
          title: 'SAT Bootcamp Post-Survey',
          body: () => (
            <Text>
              Fill out this {TUTOR_POST_SURVEY_ESTIMATED_TIME} minute post-survey for the SAT
              Bootcamp as part of the College Board research study.
            </Text>
          ),
          link: postSurveyLink,
          onMarkComplete: () =>
            RPCRefresh.markSeriesSurveyComplete({
              seriesId: hostDetails.seriesId,
              surveyType: 'tutor-post-survey',
            }),
        },
      }
    : null

  return [tutorPreSeriesSurveyTodo, tutorPostSeriesSurveyTodo].filter(isNotNull)
}

export default function useSATBootcampTutorSurveyTodos(): TodoItem[] {
  const user = useUser()
  const query = useQuery()
  const [openModalKey, setOpenModalKey] = useState<string | null>(null)

  const seriesHostDetails: TutorSeriesHostDetails[] = query
    .many(
      SeriesHostQ.where({
        userId: user.id,
        include: [
          {
            association: 'Series',
            required: true,
            where: SQL`
              ${SeriesQ.isActiveOrEndedWithinDays(DAYS_TO_INCLUDE_SERIES_AFTER_END)}
              AND ${SeriesQ.hasAnyTemplateSlug(SAT_BOOTCAMP_SERIES_TEMPLATE_SLUGS)}
            `,
          },
        ],
      })
    )
    .map(host => ({
      seriesId: host(SeriesQ.id) ?? 0,
      completedPreSeriesSurvey: host(SeriesHostQ.hasCompletedPreSeriesSurvey()) ?? false,
      completedPostSeriesSurvey: host(SeriesHostQ.hasCompletedPostSeriesSurvey()) ?? false,
      seriesStartDate: DateTime.fromISO(`${host(SeriesQ.getComputedStartDate())}`),
      seriesEndDate: DateTime.fromISO(`${host(SeriesQ.getComputedEndDate())}`),
    }))

  const surveyTodoConfigs = seriesHostDetails.flatMap(hostDetails =>
    getSurveyTodosForHost({
      hostDetails,
      uuidTraining: user.uuidTraining,
    })
  )

  const allTodos = buildSurveyTodoItems({
    configs: surveyTodoConfigs,
    worldSlug: SAT_BOOTCAMP_WORLD_SLUG,
    targetLevel: 'tutor',
    openModalKey,
    setOpenModalKey,
  })

  return allTodos.filter(todo => todo.deadline == null || DateTime.now() < todo.deadline)
}
